/*
 * Copyright 2026 Sony Corporation
 */
import { createContext, useContext } from 'react';

import type { ApiClient } from '../services/api-client';
import type { AuthService, SignInResult } from '../services/cognito';

export interface AuthContextValue {
  api: ApiClient;
  email: string | undefined;
  signedIn: boolean;
  /** In the Cognito group "admin": sees every user's usage and sets their limits. The API checks it again. */
  isAdmin?: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  /** Called once a sign-in challenge has resolved into a session. */
  confirmSignedIn: () => void;
  signOut: () => void;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return value;
};

export type { AuthService };
